import API from '../context/API';

interface AnalyticsOptions {
  startDate?: string | any;
  endDate?: string | any;
  filter?: string;
  merchantId?: string;
  store?: string;
  includeComparison?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  [key: string]: any;
}

interface ApiResponse<T = any> {
  data: T;
  [key: string]: any;
}

interface BatchFetchRequest {
  endpoint: string;
  params?: AnalyticsOptions;
  options?: Record<string, any>;
}

interface BatchFetchResult<T = any> {
  endpoint: string;
  data?: T;
  error?: any;
  success: boolean;
}

const ANALYTICS_ENDPOINTS = {
  OVERVIEW: '/analytics/overview',
  TRENDS: '/analytics/trends',
  CATEGORIES: '/analytics/categories',
  TOP_ITEMS: '/analytics/top-items',
  SALES: '/analytics/sales',
  ITEM_SALES: '/analytics/item-sales',
  CATEGORY_SALES: '/analytics/category-sales',
  PROFIT_MARGINS: '/analytics/profit-margins',
  DELIVERY_COST_ANALYSIS: '/analytics/delivery-cost-analysis',
  FEE_BREAKDOWN: '/analytics/fee-breakdown',
  MERCHANT_EARNINGS: '/merchant/{merchantId}/earnings',
  CUSTOMERS: '/analytics/customers',
  DISPUTES: '/analytics/disputes',
  POPULAR_ITEMS: '/analytics/popular-items',
  TRENDING_ITEMS: '/analytics/trending-items',
  ITEM_PERFORMANCE_COMPARISON: '/analytics/item-performance-comparison',
  ACTIVE_SESSIONS: '/analytics/active-sessions',
  DEVICES: '/analytics/devices',
  STORES: '/analytics/stores',
  MERCHANTS: '/analytics/merchants',
  STAFF: '/analytics/staff',
  MANAGERS: '/analytics/managers',
  ADMINS: '/analytics/admins'
};

const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  retryMultiplier: 2
};

const apiCallWithRetry = async (endpoint: string, params: Record<string, any> = {}, options: Record<string, any> = {}): Promise<ApiResponse> => {
  const { maxRetries, retryDelay, retryMultiplier } = RETRY_CONFIG;
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        throw new Error('No access token found');
      }

      const response = await API.get<ApiResponse>(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...params,
          _t: Date.now(),
        },
        ...options
      });

      if (!response.data) {
        throw new Error('Invalid response structure: missing data');
      }

      return response;
    } catch (error: any) {
      lastError = error;

      if (error.response?.status === 401) {
        throw error;
      }

      if (error.response?.status >= 400 && error.response?.status < 500 &&
          error.response?.status !== 408 && error.response?.status !== 429) {
        throw error;
      }

      if (attempt === maxRetries) {
        throw error;
      }

      const delay = retryDelay * Math.pow(retryMultiplier, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));

      console.warn(`API call failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
    }
  }

  throw lastError;
};

const buildAnalyticsParams = (options: AnalyticsOptions = {}): Record<string, any> => {
  const {
    startDate,
    endDate,
    filter,
    merchantId,
    store,
    includeComparison = false,
    page = 1,
    limit = 10,
    sortBy,
    sortOrder = 'desc',
    search,
    ...otherParams
  } = options;

  const params: Record<string, any> = {};

  if (startDate && endDate) {
    params.startDate = typeof startDate === 'string' ? startDate : startDate.format('YYYY-MM-DD');
    params.endDate = typeof endDate === 'string' ? endDate : endDate.format('YYYY-MM-DD');
  }

  if (filter) {
    params.filter = filter;
  }

  if (merchantId) {
    params.merchantId = merchantId;
  }
  if (store) {
    params.store = store;
  }

  if (includeComparison) {
    params.includeComparison = includeComparison;
  }

  if (page) {
    params.page = page;
  }
  if (limit) {
    params.limit = limit;
  }

  if (sortBy) {
    params.sortBy = sortBy;
    params.sortOrder = sortOrder;
  }

  if (search) {
    params.search = search;
  }

  Object.assign(params, otherParams);

  return params;
};

class AnalyticsAPI {
  static async getOverview(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.OVERVIEW, params);
    return response.data;
  }

  static async getTrends(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.TRENDS, params);
    return response.data;
  }

  static async getCategories(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.CATEGORIES, params);
    return response.data;
  }

  static async getTopItems(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.TOP_ITEMS, params);
    return response.data;
  }

  static async getSales(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.SALES, params);
    return response.data;
  }

  static async getItemSales(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ITEM_SALES, params);
    return response.data;
  }

  static async getCategorySales(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.CATEGORY_SALES, params);
    return response.data;
  }

  static async getProfitMargins(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.PROFIT_MARGINS, params);
    return response.data;
  }

  static async getDeliveryCostAnalysis(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.DELIVERY_COST_ANALYSIS, params);
    return response.data;
  }

  static async getFeeBreakdown(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.FEE_BREAKDOWN, params);
    return response.data;
  }

  static async getMerchantEarnings(merchantId: string, options: AnalyticsOptions = {}): Promise<any> {
    if (!merchantId) {
      throw new Error('Merchant ID is required');
    }

    const endpoint = ANALYTICS_ENDPOINTS.MERCHANT_EARNINGS.replace('{merchantId}', merchantId);
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(endpoint, params);
    return response.data;
  }

  static async getCustomers(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.CUSTOMERS, params);
    return response.data;
  }

  static async getDisputes(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.DISPUTES, params);
    return response.data;
  }

  static async getPopularItems(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.POPULAR_ITEMS, params);
    return response.data;
  }

  static async getTrendingItems(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.TRENDING_ITEMS, params);
    return response.data;
  }

  static async getItemPerformanceComparison(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ITEM_PERFORMANCE_COMPARISON, params);
    return response.data;
  }

  static async getActiveSessions(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ACTIVE_SESSIONS, params);
    return response.data;
  }

  static async getDevices(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.DEVICES, params);
    return response.data;
  }

  static async getStores(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.STORES, params);
    return response.data;
  }

  static async getMerchants(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.MERCHANTS, params);
    return response.data;
  }

  static async getStaff(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.STAFF, params);
    return response.data;
  }

  static async getManagers(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.MANAGERS, params);
    return response.data;
  }

  static async getAdmins(options: AnalyticsOptions = {}): Promise<any> {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ADMINS, params);
    return response.data;
  }

  static async batchFetch(endpoints: BatchFetchRequest[] = []): Promise<Record<string, BatchFetchResult>> {
    const promises = endpoints.map(async ({ endpoint, params = {}, options = {} }) => {
      try {
        const data = await (this as any)[endpoint](params);
        return { endpoint, data, success: true };
      } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);
        return { endpoint, error, success: false };
      }
    });

    const results = await Promise.allSettled(promises);

    return results.reduce((acc: Record<string, BatchFetchResult>, result, index) => {
      const { endpoint } = endpoints[index];
      if (result.status === 'fulfilled') {
        acc[endpoint] = result.value;
      } else {
        acc[endpoint] = { endpoint, error: result.reason, success: false };
      }
      return acc;
    }, {});
  }
}

export default AnalyticsAPI;
export { ANALYTICS_ENDPOINTS, RETRY_CONFIG };
export type { AnalyticsOptions, ApiResponse, BatchFetchRequest, BatchFetchResult };
